import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Button, Menu, MenuItem } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

// One shared per-language bar: a row of language pills + an optional
// "copy from <language>" menu. Replaces the two competing per-language UIs
// (custom pills vs MUI Tabs). Theme-safe (palette tokens, real MUI Menu) so it
// works in dark mode.
//
// Props:
//   langs      [{ code, short?, label }]   — already filtered to admin languages
//   activeCode string                       — the currently selected language code
//   onSelect   (code) => void
//   onCopyFrom (code) => void   optional    — shows the "copy from" menu when set
//   tr         (key, fallback) => string    optional
//   right      node                         optional — extra controls on the right
export const LanguageBar = ({ langs = [], activeCode, onSelect, onCopyFrom, tr, right }) => {
    const [anchor, setAnchor] = useState(null);
    const trx = typeof tr === 'function' ? tr : (k, fb) => fb;
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
                {langs.map((l) => {
                    const active = l.code === activeCode;
                    return (
                        <ButtonBase
                            key={l.code}
                            onClick={() => onSelect && onSelect(l.code)}
                            sx={{
                                px: 1.5, py: 0.5, borderRadius: 0.75,
                                fontWeight: 600, fontSize: '0.85rem',
                                color: active ? 'primary.main' : 'text.secondary',
                                backgroundColor: active ? 'action.selected' : 'transparent',
                                '&:hover': { backgroundColor: active ? 'action.selected' : 'action.hover' },
                            }}
                        >
                            {l.short && <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', mr: 0.5 }}>{l.short}</Box>}
                            {l.label}
                        </ButtonBase>
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {right}
                {onCopyFrom && langs.length > 1 && (
                    <>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
                            onClick={(e) => setAnchor(e.currentTarget)}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}
                        >
                            {trx('cfg_lang_copy_from', 'Kopiëren van')}…
                        </Button>
                        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                            {langs.filter(l => l.code !== activeCode).map(l => (
                                <MenuItem key={l.code} onClick={() => { onCopyFrom(l.code); setAnchor(null); }}>
                                    {l.short && <Box component="span" sx={{ color: 'text.disabled', mr: 0.75, fontSize: '0.72rem' }}>{l.short}</Box>}
                                    {l.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
            </Box>
        </Box>
    );
};

LanguageBar.propTypes = {
    langs: PropTypes.array,
    activeCode: PropTypes.string,
    onSelect: PropTypes.func,
    onCopyFrom: PropTypes.func,
    tr: PropTypes.func,
    right: PropTypes.node,
};

export default LanguageBar;
